<template>
  <section class="admin-content">
    <Navbar />

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div class="container">
        <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
          <div class="d-flex align-items-center" style="gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-thermometer" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Services &amp; Drugs</h4>
              <small style="opacity: 0.8;">Add and manage services or drugs for this claim</small>
            </div>
          </div>
          <button class="btn btn-outline-light btn-sm" @click="singleClaim()">
            <i class="fe fe-arrow-right mr-1"></i> Proceed to Documents
          </button>
        </div>
      </div>
    </div>

    <section style="margin-top: -32px;">
      <div class="container pb-5">

        <!-- ── Add Service / Drug Card ── -->
        <div class="card shadow table-card mb-4">
          <div class="form-card-header">
            <div class="section-number"><i class="fe fe-plus" style="font-size: 12px;"></i></div>
            <h6 class="mb-0 font-weight-bold text-white">Add Service / Drug</h6>
          </div>
          <div class="card-body px-4 py-4">
            <form @submit.prevent="addServiceRendered">
              <div class="row">
                <div class="form-group col-md-6">
                  <label class="font-weight-bold" style="font-size: 0.85rem;">Type</label>
                  <select class="form-control" v-model="type" required @change="handleData">
                    <option value="" disabled>Select type…</option>
                    <option value="Drug">Drug</option>
                    <option value="Service">Service</option>
                  </select>
                </div>

                <div class="form-group col-md-6" v-if="type == 'Service'">
                  <label class="font-weight-bold" style="font-size: 0.85rem;">Service</label>
                  <v-select v-model="addservice.services_id" @change="getService()" label="description"
                    :required="!addservice.services_id" :options="serviceOptions" placeholder="Search service…" />
                </div>

                <div class="form-group col-md-6" v-if="type == 'Drug'">
                  <label class="font-weight-bold" style="font-size: 0.85rem;">Drug </label>
                  <v-select v-model="addservice.drugs_id" label="item_data" :required="!addservice.drugs_id"
                    :options="drugOptions" placeholder="Search drug…" />
                </div>

                <!-- Mode toggle -->
                <div class="col-md-12 mb-3" v-if="type != ''">
                  <div class="btn-group btn-group-sm" role="group">
                    <button type="button" class="btn"
                      :class="doseMode === 'simple' ? 'btn-info' : 'btn-outline-secondary'"
                      @click="doseMode = 'simple'">
                      <i class="fe fe-box mr-1"></i> Quantity
                    </button>
                    <button type="button" class="btn"
                      :class="doseMode === 'advanced' ? 'btn-info' : 'btn-outline-secondary'"
                      @click="doseMode = 'advanced'">
                      <i class="fe fe-sliders mr-1"></i> Dose / Freq / Days
                    </button>
                  </div>
                </div>

                <!-- Simple mode: Quantity only -->
                <div class="form-group col-md-4" v-if="type != '' && doseMode === 'simple'">
                  <label class="font-weight-bold" style="font-size: 0.85rem;">Quantity</label>
                  <input type="number" min="1" :max="serviceMaxDays || undefined"
                    class="form-control" required placeholder="1" v-model="days" />
                  <small v-if="serviceMaxDays" class="text-warning font-weight-bold">
                    <i class="fe fe-alert-circle mr-1"></i> Max {{ serviceMaxDays }} allowed
                  </small>
                </div>

                <!-- Advanced mode: Dose / Frequency / Days -->
                <template v-if="type != '' && doseMode === 'advanced'">
                  <div class="form-group col-md-4" v-if="type == 'Drug'">
                    <label class="font-weight-bold" style="font-size: 0.85rem;">Dose</label>
                    <input type="text" class="form-control" required placeholder="1" v-model="dose" />
                  </div>

                  <div class="form-group col-md-4">
                    <label class="font-weight-bold" style="font-size: 0.85rem;">Frequency</label>
                    <input v-if="type == 'Service'" type="number" min="1" class="form-control" required
                      placeholder="e.g. 2" v-model="frequency" />
                    <select v-else class="form-control" v-model="frequency">
                      <option value="1">Daily (OD)</option>
                      <option value="2">Twice daily (BD)</option>
                      <option value="3">Three times (TDS)</option>
                      <option value="4">Four times (QDS)</option>
                    </select>
                    <small class="text-muted">Times per day</small>
                  </div>

                  <div class="form-group col-md-4">
                    <label class="font-weight-bold" style="font-size: 0.85rem;">Duration (Days)</label>
                    <input type="number" min="1" :max="serviceMaxDays || 50" class="form-control" required
                      placeholder="e.g. 5" v-model="days" />
                    <small v-if="serviceMaxDays" class="text-warning font-weight-bold">
                      <i class="fe fe-alert-circle mr-1"></i> Max {{ serviceMaxDays }} days allowed
                    </small>
                    <small v-else class="text-muted">For admissions, bedrest etc.</small>
                  </div>
                </template>
              </div>

              <button type="submit" class="btn btn-submit">
                <i class="fe fe-plus mr-1"></i> Add to Claim
              </button>
            </form>
          </div>
        </div>

        <!-- ── Services / Drugs Table ── -->
        <div class="card shadow table-card mb-4">
          <div class="d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
            <div class="d-flex align-items-center">
              <i class="fe fe-activity text-primary mr-2"></i>
              <h6 class="mb-0 font-weight-bold">Claim Services / Drugs</h6>
              <span v-if="singleclaim.services" class="badge badge-soft-info ml-2">
                {{ singleclaim.services.length }}
              </span>
            </div>
          </div>
          <div class="table-responsive">
            <table class="table align-td-middle mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th">#</th>
                  <th class="table-th">Name</th>
                  <th class="table-th">Dose</th>
                  <th class="table-th">Frequency</th>
                  <th class="table-th">Days / Qty</th>
                  <th class="table-th">Unit Cost</th>
                  <th class="table-th">Total Cost</th>
                  <th class="table-th"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(service, index) in singleclaim.services" :key="service.id" class="table-row">
                  <td class="px-4 py-3">{{ index + 1 }}</td>
                  <td class="py-3">
                    <span v-if="service.service != null">{{ service.service.description }}</span>
                    <span v-if="service.drugs_id != null">{{ service.drug.drug_name }}</span>
                    <span v-if="service.drugs_id != null" class="badge badge-soft-info ml-1"
                      style="font-size: 0.7rem;">Drug</span>
                  </td>
                  <td class="py-3">{{ service.dose }}</td>
                  <td class="py-3">{{ service.frequency }}</td>
                  <td class="py-3">{{ service.days }}</td>
                  <td class="py-3">
                    &#8358;
                    <span v-if="service.service != null">{{ service.service.price }}</span>
                    <span v-if="service.drugs_id != null">{{ service.drug.price }}</span>
                  </td>
                  <td class="py-3 font-weight-bold">&#8358;{{ service.cost | numeral(0, 0) }}</td>
                  <td class="py-3">
                    <button class="btn btn-sm btn-outline-danger"
                      @click="deleteService(service)">
                      <i class="fe fe-trash-2"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="!singleclaim.services || !singleclaim.services.length">
                  <td colspan="8" class="text-center py-5 text-muted">
                    <i class="fe fe-inbox" style="font-size: 2rem; opacity: 0.3;"></i>
                    <p class="mt-2 mb-0">No services added yet</p>
                  </td>
                </tr>
                <tr v-if="singleclaim.services && singleclaim.services.length" class="bg-light font-weight-bold">
                  <td colspan="6" class="px-4 py-3">Total</td>
                  <td class="py-3">&#8358;{{ singleclaim.sum | numeral(0, 0) }}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── Proceed footer ── -->
        <div class="d-flex justify-content-end">
          <button class="btn btn-submit" @click="singleClaim()">
            Proceed to Document Upload <i class="fe fe-arrow-right ml-1"></i>
          </button>
        </div>

      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </section>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  components: {
    Navbar,
    Loading,
  },
  data() {
    return {
      user: null,
      services: "",
      singleclaim: "",
      drugs: "",
      days: 1,
      dose: 1,
      frequency: 1,
      cost: "",
      singleservice: "",
      singledrug: "",
      claims: "",
      edit: false,
      isLoading: false,
      fullPage: true,
      type: "",
      selected_type: "",
      doseMode: "simple",
      addservice: {
        services_id: "",
        drugs_id: "",
      },
    };
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.axios
      .get(`/api/v1/auth/service-agency/90`)
      .then((response) => {
        this.services = response.data;
      })
      .catch((error) => {
        console.error(error);
      });
  },
  computed: {
    serviceOptions() {
      return (this.services && this.services.data) ? this.services.data : [];
    },
    drugOptions() {
      return (this.drugs && this.drugs.data) ? this.drugs.data : [];
    },
    serviceMaxDays() {
      if (
        this.type === "Service" &&
        this.addservice.services_id &&
        this.addservice.services_id.max_days != null
      ) {
        const n = Number(this.addservice.services_id.max_days);
        return isNaN(n) ? null : n;
      }
      return null;
    },
    calcCost() {
      if (this.type == "Service") {
        return this.quantity * this.addservice.services_id.price;
      } else {
        return this.quantity * this.addservice.drugs_id.price;
      }
    },
  },
  methods: {
    deleteService(service) {
      if (confirm("Are you sure you want to delete permanently?")) {
        this.axios
          .delete(`/api/v1/auth/claim_service/${service.id}`)
          .then(() => {
            this.getSingleClaim();
          })
          .catch((error) => {
            console.error(error);
          });
      }
    },

    handleData() {
      if (this.type == "Drug") {
        this.addservice.services_id = "";
      }
      if (this.type == "Service") {
        this.addservice.drugs_id = "";
      }
      this.doseMode = "simple";
      this.dose = 1;
      this.frequency = 1;
      this.days = 1;
    },

    singleClaim() {
      this.$router.push(`/upload-claims-docs/${this.$route.params.id}`);
    },

    getDrugs() {
      this.axios
        .get(`/api/v1/auth/drug-agency/90`)
        .then((response) => {
          this.drugs = response.data;
        })
        .catch((error) => {
          console.error(error);
        });
    },

    clearIt() {
      this.addservice.services_id = "";
      this.addservice.drugs_id = "";
      this.quantity = 1;
      this.days = 1;
      this.dose = 1;
      this.frequency = 1;
    },

    getSingleClaim() {
      this.axios
        .get(`/api/v1/auth/detailedClaim/${this.$route.params.id}`)
        .then((response) => {
          this.singleclaim = response.data;
        })
        .catch((error) => {
          console.error(error);
        });
    },

    getService() {
      this.axios
        .get(`/api/v1/auth/services/${this.addservice.services_id.id}`)
        .then((response) => {
          this.singleservice = response.data;
        })
        .catch((error) => {
          console.error(error);
        });
    },

    getDrug() {
      this.axios
        .get(`/api/v1/auth/drugs/${this.addservice.drugs_id}`)
        .then((response) => {
          this.singledrug = response.data[0];
        })
        .catch((error) => {
          console.error(error);
        });
    },

    addServiceRendered() {
      this.user = JSON.parse(localStorage.getItem("user"));
      if (this.serviceMaxDays && Number(this.days) > this.serviceMaxDays) {
        this.$toasted.error(
          `Maximum allowed is ${this.serviceMaxDays} days / units for this service`,
          { position: "top-center", duration: 4000 }
        );
        return;
      }
      this.isLoading = true;
      const dose = this.doseMode === "simple" ? 1 : this.dose;
      const frequency = this.doseMode === "simple" ? 1 : this.frequency;
      this.axios
        .post("/api/v1/auth/claim_service", {
          services_id: this.addservice.services_id.id,
          drugs_id: this.addservice.drugs_id.id,
          claims_id: this.$route.params.id,
          dose,
          frequency,
          days: this.days,
        })
        .then(() => {
          this.clearIt();
          this.getSingleClaim();
          this.isLoading = false;
          this.$toasted.success("Added Successfully!", { position: "top-center", duration: 3000 });
        })
        .catch((error) => {
          console.log(error.response);
          this.isLoading = false;
        });
    },
  },
  created() {
    this.getDrugs();
    this.getSingleClaim();
  },
};
</script>

<style scoped>
.form-control:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 0.15rem rgba(37, 99, 235, 0.15);
}
</style>
